// The live tuning knobs of the world passes (plan Part B, §B.7).
//
// The owner's eye test compared the shader against the renderer of before
// R505, side by side at three zoom bands. Every figure that decision could
// turn on is here, read from `location.hash` and re-read on `hashchange`, so
// a comparison never costs a reload:
//
//   #tr=1.8 bw=1 inset=1.5 pulse=1 hach=1 tick=1
//    ringstep=0.155 terrain=tex edt=4 dtap=bspline heat=smooth clear=1
//    blend=1 early=plane rock=plane
//    fog=1 snow=1 lit=1 haze=1 whales=1 kraken=1 beast=0 silt=1 tremor=1
//    wake=1 hex=1 fogn=1 snown=1 siltn=1 whalen=1 waken=1 hazeb=2
//    chart=all effects=1 fxfleet=0 fxround=0 fxhold=0 gather=1
//
// The second block is the fluff table of the parity plan of 2026-09-04. It
// is not an eye-test knob of the world passes but a per-piece switch of the
// decoration, so a measurement can price one piece at a time.
//
// `clear` and `blend` are measurement knobs of the frame loop rather than
// eye-test knobs (R431). `early`, `rock` and `heat` were measurement
// knobs as well until the owner's eye test of 2026-09-04 made their treated
// value the default (R493); the OTHER value of each one now states what the
// client did before, and a measurement or an eye test selects it. `?aa=1`
// and `?dpr=N` sit beside them in `readQueryKnobs` below, because a context
// attribute cannot be re-read.
//
// The defaults ARE the shipping figures. `ringstep` takes the generated
// `TERRAIN_RING_STEP`, because a knob's default must be the data layer's
// value and not a second copy of it.

import {
  TERRAIN_FIELD_TEXEL_PX, TERRAIN_RING_STEP, TERRITORY_GEO_INSET_PX,
} from '../gen/presentation';
import { TERRAIN_RADIUS_K } from './shaders/prelude';
import { DEFAULT_RENDERER, type RendererPref } from './gfx/select';

/**
 * Where the TERRAIN fields come from (R432).
 *
 * `tex` reads `fT` and `fR` from the terrain plane the hello prepared.
 * `analytic` sums the kernel over the seven cells of ring 1 at every screen
 * pixel, which is what the pass did before R432. The layer draws with
 * `analytic` until the prepare lands, whichever the knob says.
 */
export type TerrainMode = 'tex' | 'analytic';
/**
 * How the two distance channels of the terrain plane are read (R432).
 *
 * `bspline` is a cubic B-spline in four bilinear taps, which is C² and has
 * no facet. `bilinear` is the one tap the pass used before, whose iso-lines
 * are polygons at the texel pitch — the void core's concentric facets.
 */
export type DistTap = 'bspline' | 'bilinear';
/**
 * How the heatmap reads its per-cell intensity.
 *
 * `smooth` is the default since 2026-09-04 (R493). It interpolates the
 * intensity across the cell, and the owner ruled on the look. It costs 26 to
 * 31 % of the frame where the heatmap draws (R484 item 5). `flat` gives each
 * cell one value, which is what the pass did before.
 */
export type HeatKnob = 'flat' | 'smooth';
/**
 * How the terrain composite answers "is any terrain in reach of this pixel?"
 *
 * `plane` is the default since 2026-09-04 (R493). It asks the terrain plane —
 * one bilinear tap, whose fT and fR channels are zero everywhere the kernel
 * reaches no terrain — and the main body reads the same tap. `fetch` runs
 * `anyTerrain` instead: seven `texelFetch` of the blocked plane over the cell
 * and its six neighbours, which is what the pass did before. Only the
 * composite has a plane, so `#terrain=analytic` keeps the scan whatever this
 * knob says.
 */
export type EarlyMode = 'fetch' | 'plane';
/**
 * Where the territory pass's rock mask gets its field.
 *
 * `plane` is the default since 2026-09-04 (R493). It reads fR off the terrain
 * plane's A channel, the way the terrain composite does. `kernel` cuts the
 * outline wash by no rock at all, and the wash draws so until the plane
 * lands a moment after the hello.
 */
export type RockMode = 'kernel' | 'plane';

export interface Knobs {
  /** The terrain kernel radius, as a multiple of HEX_SIZE. */
  tr: number;
  /** A scale on every border pass width. */
  bw: number;
  /** How far the outline border's glow moves into its own claim, in
   *  world px (R497). */
  inset: number;
  /** 0 freezes the border pulse, which is what a still comparison wants. */
  pulse: 0 | 1;
  /** 0 turns the trench hachures off. */
  hach: 0 | 1;
  /** A scale on every hachure tick length. */
  tick: number;
  /** The spacing of the ridge contour rings, as a fraction of the depth. */
  ringstep: number;
  /** Where the terrain fields come from. */
  terrain: TerrainMode;
  /** The terrain plane's texel pitch in world px. */
  edt: 2 | 4;
  /** How the terrain plane's two distance channels are read. */
  dtap: DistTap;
  /** The heatmap mode. `smooth` is the default since R493. */
  heat: HeatKnob;
  /** 0 drops `gl.clear` where the opaque background paints the same pixels. */
  clear: 0 | 1;
  /** 0 takes BLEND off for the background pass alone. */
  blend: 0 | 1;
  /**
   * How the terrain composite tests for terrain in reach. `plane` is the
   * default since R493, and `fetch` is the seven-fetch scan it replaces.
   */
  early: EarlyMode;
  /**
   * Where the territory pass's rock mask gets its field. `plane` is the
   * default since R493, and `kernel` is the scan it replaces.
   */
  rock: RockMode;

  // -- The fluff knobs ------------------------------------------------------
  //
  // One knob a decorative piece, from the fluff table of the parity plan of
  // 2026-09-04. Each one gates the piece the OPTIONS `fluff` switch names,
  // and that switch is the umbrella: `fluff` off takes every piece off
  // whatever these say, and `fluff` on lets the knobs decide. The default of
  // every one is on, so a page that states none draws what the client ships,
  // except `fog` and `haze`: the owner took the fog blobs and the edge haze
  // out of the shipped look (R2989), and `#fog=1` or `#haze=1` puts one back.
  // Phase C of the parity plan measures them one row a knob on the frozen
  // world, and the owner tunes the defaults from that table.
  //
  // The count knobs (`fogn`, `snown`, `siltn`, `whalen`, `waken`) multiply
  // the count the piece would otherwise take and are clamped at 0. The
  // floors of the counted pieces stay: a multiplier trims the crowd, it does
  // not empty the world. `hazeb` is a band count and not a multiplier.

  /** 0 takes the fog blobs off. */
  fog: 0 | 1;
  /** 0 takes the marine snow off. */
  snow: 0 | 1;
  /** 0 takes the ping-lit pass over the snow grains off. */
  lit: 0 | 1;
  /** 0 takes the edge turbidity bands off. */
  haze: 0 | 1;
  /** 0 takes the whales off. */
  whales: 0 | 1;
  /** 0 takes the trench kraken off. */
  kraken: 0 | 1;
  /**
   * Which pieces of the chart furniture draw, for an eye test one piece
   * at a time (R548, ui-map-interface.md §17.2).
   *
   * `all` (the default) lets the OPTIONS setting and every piece gate
   * decide. `none` draws no piece. A list of piece ids joined by `+`
   * (`chart=isobaths+graticule`; a comma is a pair separator of the hash)
   * draws those pieces alone. The knob sits
   * UNDER the setting and the gates: it can take a piece off, never put
   * one on that the reader or the table refused.
   */
  chart: string;
  /**
   * Which of the four beasts of the old charts rises (R525).
   *
   * 0 lets the layer's own stream pick, which is what the client ships;
   * 1 to 4 force one, so an eye test can meet the same animal twice.
   */
  beast: 0 | 1 | 2 | 3 | 4;
  /** 0 takes the seaquake silt off. */
  silt: 0 | 1;
  /** 0 takes the seaquake camera shudder off, and the clock keeps running. */
  tremor: 0 | 1;
  /** 0 takes the own-hull bioluminescent wake off. */
  wake: 0 | 1;
  /** 0 refuses the hex fill and hex border views. */
  hex: 0 | 1;
  /**
   * 0 takes the player effects off, both slots (R914, §6.6).
   *
   * It is a measurement and eye-test knob and NOT a viewer setting: a
   * viewer cannot switch another player's mark off by the owner's brief.
   * The one switch a viewer has is the OS reduced-motion switch, which
   * freezes the mark and keeps it (R915).
   */
  effects: 0 | 1;
  /**
   * An eye-test knob per kind: a catalog wire id that every fleet, every
   * round or every hold on the map wears instead of its own effect, 0 for
   * none. It shows one recipe in the real renderer with no grant and no
   * join, so a recipe can be judged against its mockup card. It is never a
   * viewer setting: the hash is the only way in, and it changes nothing on
   * the wire.
   */
  fxfleet: number;
  fxround: number;
  fxhold: number;
  /**
   * 0 takes the gun shots and the hits off, and the caller draws its own ring
   * and boom instead (R1007, R1009).
   *
   * It is a measurement and eye-test knob and NOT a viewer setting, exactly as
   * `effects` is: a viewer must not switch another player's fight off.
   */
  shots: 0 | 1;
  /**
   * An eye-test knob per gun field: one look id every fight of that field
   * wears instead of the theme's, '' for none. It shows one recipe in the real
   * renderer with no theme and no join, so a card can be judged against its
   * mockup page. It is never a viewer setting: the hash is the only way in,
   * and it changes nothing on the wire. It is the pattern of `fxfleet` (R925).
   */
  shotmelee: string;
  shotartillery: string;
  shotsentinel: string;
  hitshield: string;
  hithull: string;
  hitsink: string;
  /**
   * `gather=0` takes the three Dredger harvesting passes off, and the map
   * draws what it drew before: a hauler on a field and a hauler adrift read
   * the same (R1120).
   *
   * It is a measurement and eye-test knob and NOT a viewer setting, exactly
   * as `shots` is.
   */
  gather: 0 | 1;
  /**
   * An eye-test knob for the harvesting look: one look id every cut wears
   * instead of the theme's, '' for none. It is the sibling of `shotmelee`,
   * and it is never a viewer setting.
   */
  gatherlook: string;
  /**
   * An eye-test knob for the anchoring look (R1125): one look id every anchor
   * hull wears instead of the theme's, '' for none. It is the sibling of
   * `gatherlook`, and it is never a viewer setting.
   */
  anchoring: string;
  /**
   * An eye-test knob for the hold-fire look (R1439): one look id every own
   * hull that holds fire wears instead of the theme's, '' for none. It is
   * the sibling of `anchoring`, and it is never a viewer setting.
   */
  holdfire: string;
  /**
   * The tube reload look (R1474): 0 takes it off every own hull with
   * tubes, the sibling of `gather`. It is never a viewer setting.
   */
  reload: 0 | 1;
  /**
   * An eye-test knob for the tube reload look (R1474): one look id every
   * own hull with tubes wears instead of the theme's, '' for none. It is
   * the sibling of `gatherlook`, and it is never a viewer setting.
   */
  reloadlook: string;
  /**
   * An eye-test knob for the detonation look (R2079): one look id every
   * round that goes off draws instead of the theme's, '' for none. It is
   * the sibling of `reloadlook`, and it is never a viewer setting.
   */
  detonationlook: string;
  /** A multiplier on the fog blob count. */
  fogn: number;
  /** A multiplier on the marine snow grain count. */
  snown: number;
  /** A multiplier on the seaquake silt grain cap. */
  siltn: number;
  /** A multiplier on the whale count. */
  whalen: number;
  /** A multiplier on the wake flare cap. */
  waken: number;
  /** How many turbidity bands an edge carries: 2 ships, 1 is the cheap one. */
  hazeb: 1 | 2;
}

/** The shipping figures, which are what the knobs default to. */
export const DEFAULT_KNOBS: Readonly<Knobs> = Object.freeze({
  tr: TERRAIN_RADIUS_K,
  bw: 1,
  inset: TERRITORY_GEO_INSET_PX,
  pulse: 1,
  hach: 1,
  tick: 1,
  ringstep: TERRAIN_RING_STEP,
  terrain: 'tex',
  edt: TERRAIN_FIELD_TEXEL_PX as 2 | 4,
  dtap: 'bspline',
  heat: 'smooth',
  clear: 1,
  blend: 1,
  early: 'plane',
  rock: 'plane',
  fog: 0,
  snow: 1,
  lit: 1,
  haze: 0,
  whales: 1,
  kraken: 1,
  chart: 'all',
  beast: 0,
  silt: 1,
  tremor: 1,
  wake: 1,
  hex: 1,
  effects: 1,
  shots: 1,
  shotmelee: '',
  shotartillery: '',
  shotsentinel: '',
  hitshield: '',
  hithull: '',
  hitsink: '',
  gather: 1,
  gatherlook: '',
  anchoring: '',
  holdfire: '',
  reload: 1,
  reloadlook: '',
  detonationlook: '',
  fxfleet: 0,
  fxround: 0,
  fxhold: 0,
  fogn: 1,
  snown: 1,
  siltn: 1,
  whalen: 1,
  waken: 1,
  hazeb: 2,
} as const);

/**
 * A key=value pair map from a hash, whatever separates the pairs.
 *
 * `%20` is a separator as well (R432). Writing `location.hash = 'a=1 b=2'`
 * stores `#a=1%20b=2`, because a browser percent-encodes the space; a split
 * on whitespace alone then read ONE pair and silently dropped every knob
 * after the first. Every hash typed by hand and every hash set by a
 * measurement script is affected, so the separator set carries the encoded
 * form beside the literal one.
 */
function pairs(hash: string): Map<string, string> {
  const out = new Map<string, string>();
  const body = hash.startsWith('#') ? hash.slice(1) : hash;
  for (const raw of body.split(/(?:[\s&,;]|%20)+/i)) {
    if (raw === '') continue;
    const eq = raw.indexOf('=');
    if (eq <= 0) continue;
    out.set(decodeURIComponent(raw.slice(0, eq)).toLowerCase(),
            decodeURIComponent(raw.slice(eq + 1)));
  }
  return out;
}

function num(v: string | undefined, fallback: number): number {
  if (v === undefined) return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * A count multiplier: a number the caller scales a crowd by, clamped at 0.
 *
 * A negative multiplier would ask a piece for a negative count, which every
 * caller would then have to guard. The clamp lives here instead, so `fogn=-3`
 * reads as "no fog" and nothing downstream repeats the test.
 */
function count(v: string | undefined, fallback: number): number {
  return Math.max(0, num(v, fallback));
}

/**
 * A look id from a knob: kebab-case, lower case, and '' for anything else.
 *
 * The registry answers an id it does not know with the AUTO look of the field,
 * so a typed hash never takes the fight away.
 */
function lookID(v: string | undefined): string {
  if (v === undefined) return '';
  const s = v.trim().toLowerCase();
  return /^[a-z][a-z0-9-]*$/.test(s) ? s : '';
}

function pick<T extends string | number>(
  v: string | undefined, allowed: readonly T[], fallback: T,
): T {
  if (v === undefined) return fallback;
  const want = typeof allowed[0] === 'number' ? Number(v) : v.toLowerCase();
  for (const a of allowed) if (a === want) return a;
  return fallback;
}

/** The knobs whose type is exactly `T`: the flags, the figures, the free strings. */
type KnobOf<T> = {
  [K in keyof Knobs]: [Knobs[K], T] extends [T, Knobs[K]] ? K : never;
}[keyof Knobs];

/**
 * The knobs a hash states, every unstated one at its default. A value that
 * does not parse, or that is not one of the choices a knob has, is ignored
 * rather than refused: a typed hash must never take the map away.
 */
export function readKnobs(hash: string = typeof location === 'undefined' ? '' : location.hash): Knobs {
  const p = pairs(hash);
  const d = DEFAULT_KNOBS;
  // One reader a knob family. Each one names its knob once.
  const choice = <K extends keyof Knobs>(k: K, allowed: readonly Knobs[K][]) =>
    pick<Knobs[K]>(p.get(k), allowed, d[k]);
  const flag = (k: KnobOf<0 | 1>) => choice(k, [0, 1]);
  const real = (k: KnobOf<number>) => num(p.get(k), d[k]);
  const mult = (k: KnobOf<number>) => count(p.get(k), d[k]);
  /** A preview wire id: a whole count, 0 for none. */
  const whole = (k: KnobOf<number>) => Math.floor(mult(k));
  const look = (k: KnobOf<string>) => lookID(p.get(k));
  return {
    tr: real('tr'),
    bw: real('bw'),
    inset: real('inset'),
    pulse: flag('pulse'),
    hach: flag('hach'),
    tick: real('tick'),
    ringstep: real('ringstep'),
    terrain: choice('terrain', ['tex', 'analytic']),
    edt: choice('edt', [2, 4]),
    dtap: choice('dtap', ['bspline', 'bilinear']),
    heat: choice('heat', ['flat', 'smooth']),
    clear: flag('clear'),
    blend: flag('blend'),
    early: choice('early', ['fetch', 'plane']),
    rock: choice('rock', ['kernel', 'plane']),
    fog: flag('fog'),
    snow: flag('snow'),
    lit: flag('lit'),
    haze: flag('haze'),
    whales: flag('whales'),
    kraken: flag('kraken'),
    chart: (p.get('chart') ?? d.chart).trim().toLowerCase() || d.chart,
    beast: choice('beast', [0, 1, 2, 3, 4]),
    silt: flag('silt'),
    tremor: flag('tremor'),
    wake: flag('wake'),
    hex: flag('hex'),
    effects: flag('effects'),
    shots: flag('shots'),
    shotmelee: look('shotmelee'),
    shotartillery: look('shotartillery'),
    shotsentinel: look('shotsentinel'),
    hitshield: look('hitshield'),
    hithull: look('hithull'),
    hitsink: look('hitsink'),
    gather: flag('gather'),
    gatherlook: look('gatherlook'),
    anchoring: look('anchoring'),
    holdfire: look('holdfire'),
    reload: flag('reload'),
    reloadlook: look('reloadlook'),
    detonationlook: look('detonationlook'),
    fxfleet: whole('fxfleet'),
    fxround: whole('fxround'),
    fxhold: whole('fxhold'),
    fogn: mult('fogn'),
    snown: mult('snown'),
    siltn: mult('siltn'),
    whalen: mult('whalen'),
    waken: mult('waken'),
    hazeb: choice('hazeb', [1, 2]),
  };
}

/**
 * The three knobs the hash cannot carry (R431).
 *
 * `antialias` is a context attribute, the device pixel ratio decides the
 * size of the first backing store, and the backend is chosen before the
 * canvas is claimed. All three are read before the surface exists and none
 * can change without a reload. They therefore come from the query string,
 * where a reload keeps them.
 *
 * `aa` and `dpr` are measurement knobs and not settings: the default IS what
 * the client ships, and no look change comes from either one. Since R491 the
 * client ships without multisampling, so `aa` defaults to false. `dpr` is the
 * cap of the device ratio, and the reel recorder, the visual diff, the store
 * shots and the throughput tools state it; the player's own lever is the
 * render scale of the settings, a share of that cap (R484 item 2,
 * `render/dpr.ts`). `renderer` picks the backend by hand; `webgl` is an alias
 * of `webgl2` (R190's spelling), and anything else is the default.
 */
export interface QueryKnobs {
  /** true where `?aa=1` asks for a multisampled context. */
  aa: boolean;
  /** The device pixel ratio cap `?dpr=N` states, or null for the built-in one. */
  dpr: number | null;
  /** Which backend `?renderer=` asks for. */
  renderer: RendererPref;
}

/** Reads the three query knobs from a search string, such as `location.search`. */
export function readQueryKnobs(
  search: string = typeof location === 'undefined' ? '' : location.search,
): QueryKnobs {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search);
  } catch {
    return { aa: false, dpr: null, renderer: DEFAULT_RENDERER };
  }
  const dpr = Number(params.get('dpr'));
  return {
    aa: params.get('aa') === '1',
    dpr: Number.isFinite(dpr) && dpr > 0 ? dpr : null,
    renderer: readRenderer(params.get('renderer')),
  };
}

/** `webgl` is an alias of `webgl2`; anything else is the default. */
function readRenderer(v: string | null): RendererPref {
  // compat(R847, 2026-12-10)
  if (v === 'webgl') return 'webgl2';
  if (v === 'auto' || v === 'webgpu' || v === 'webgl2') return v;
  return DEFAULT_RENDERER;
}

/**
 * Calls back with the knobs now, and again on every `hashchange`. Returns
 * the function that stops it.
 */
export function onKnobs(cb: (k: Knobs) => void): () => void {
  cb(readKnobs());
  if (typeof window === 'undefined') return () => {};
  const listener = () => cb(readKnobs());
  window.addEventListener('hashchange', listener);
  return () => window.removeEventListener('hashchange', listener);
}

/**
 * The pieces a `chart` knob lets through: null for every piece, else the
 * set of ids it names (an empty set for `none`). The ids are joined by
 * `+`, because the hash splits its pairs on a comma. A piece id is a
 * `chart.toml` slug, `isobaths`, `border_band`; a hyphen reads as its
 * underscore so the address bar may carry either.
 */
export function chartOnly(knob: string): ReadonlySet<string> | null {
  const v = knob.trim().toLowerCase();
  if (v === '' || v === 'all') return null;
  if (v === 'none') return new Set();
  return new Set(v.split('+').map((x) => x.trim().replace(/-/g, '_')).filter((x) => x !== ''));
}
